import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminAuthGuard from "../components/admin/AdminAuthGuard";
import AdminLayout from "../components/admin/AdminLayout";
import {
  getAdminAudit,
  getAdminEnv,
  simulateAdminPayment,
} from "../lib/api/index";
import { logger } from "../lib/logger";

function Inner({ admin }) {
  const { sessionId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  // Verification tooling — admin "Simulate Successful Payment" state.
  // Surfaces are mutually exclusive: button | confirm prompt | success card.
  // Hidden entirely in production and on already-paid sessions.
  const [env, setEnv] = useState(null);
  const [simulationPhase, setSimulationPhase] = useState("idle"); // idle | confirming | submitting | success | error
  const [simulationErr, setSimulationErr] = useState(null);
  const [simulationResult, setSimulationResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getAdminAudit(sessionId)
      .then((data) => { if (!cancelled) setData(data); })
      .catch((e) => {
        logger.error("admin_audit_detail_failed", { status: e?.response?.status });
        if (!cancelled) setErr(e?.response?.status === 404 ? "Audit not found." : "Could not load audit.");
      });
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    // One round-trip per page mount. Backend is the authority on whether
    // the simulation surface is allowed; the UI never decides on its own.
    let cancelled = false;
    getAdminEnv()
      .then((data) => { if (!cancelled) setEnv(data); })
      .catch((e) => { logger.error("admin_env_failed", { status: e?.response?.status }); });
    return () => { cancelled = true; };
  }, []);

  const onSimulatePayment = async () => {
    if (simulationPhase === "submitting") return;
    setSimulationPhase("submitting");
    setSimulationErr(null);
    try {
      const data = await simulateAdminPayment(sessionId);
      setSimulationResult(data);
      setSimulationPhase("success");
      // Refresh the detail payload so `paid: yes` shows up everywhere
      getAdminAudit(sessionId).then((d) => setData(d)).catch(() => {});
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403 || detail?.error === "forbidden_in_production") {
        setSimulationErr("This action is disabled in production.");
      } else {
        logger.error("simulate_payment_failed", { status, detail });
        setSimulationErr("Could not simulate payment. Check backend logs.");
      }
      setSimulationPhase("error");
    }
  };

  if (err) {
    return <AdminLayout admin={admin}><div className="sf-card" data-testid="admin-audit-detail-error">{err}</div></AdminLayout>;
  }
  if (!data) {
    return <AdminLayout admin={admin}><div className="sf-muted-text" data-testid="admin-audit-detail-loading">Loading audit…</div></AdminLayout>;
  }

  const s = data.session;
  const r = data.report;
  const p = data.payment;
  const ir = data.implementation_request;

  return (
    <AdminLayout admin={admin}>
      <div data-testid="admin-audit-detail-page">
        <Link to="/admin/audits" className="sf-mono sf-muted-text" style={{ fontSize: 12, textDecoration: "none" }}>
          ← back to audits
        </Link>
        <div className="sf-eyebrow" style={{ marginTop: 10, marginBottom: 8 }}>Audit detail</div>
        <h1 className="sf-display" style={{ fontSize: 28, marginBottom: 4 }} data-testid="admin-audit-detail-business">
          {s.business_name || "(no name)"}
        </h1>
        <div className="sf-mono sf-muted-text" style={{ fontSize: 12, marginBottom: 20 }}>
          session_id: {s.session_id} · status: {s.status} · paid: {s.paid ? "yes" : "no"}
        </div>

        {/* Session card */}
        <div className="sf-card" style={{ marginBottom: 18 }} data-testid="admin-detail-session">
          <div className="sf-eyebrow" style={{ marginBottom: 8 }}>Session</div>
          <div className="sf-mono" style={{ fontSize: 12, lineHeight: 1.7 }}>
            <div>Email: {s.email || "—"}</div>
            <div>Created: {s.created_at}</div>
            <div>Completed: {s.completed_at || "—"}</div>
            <div>Paid at: {s.paid_at || "—"}</div>
            <div>IP: {s.ip_address || "—"}</div>
            <div>User agent: {(s.user_agent || "—").slice(0, 120)}{(s.user_agent || "").length > 120 ? "…" : ""}</div>
            {s._seeded && <div style={{ color: "var(--sf-gold)" }}>(seeded sample audit)</div>}
          </div>
        </div>

        {/* Phase-5a verification — "Simulate Successful Payment" admin tool.
            Visible ONLY when:
              - The session is not already paid
              - Backend reports payment_simulation_enabled (i.e. ENV != production)
            The button POSTs to /admin/audits/{sid}/simulate-payment which
            synthesizes the exact state the real Stripe webhook would
            produce, then spawns write_paid_report_for_session as a task. */}
        {!s.paid && env?.payment_simulation_enabled && (
          <div
            className="sf-card"
            data-testid="admin-simulate-payment"
            style={{ marginBottom: 18, borderColor: "var(--sf-gold-dim)" }}
          >
            <div className="sf-eyebrow" style={{ marginBottom: 8 }}>
              Verification tooling &middot; {env.env}
            </div>
            <div className="sf-mono sf-muted-text"
                 style={{ fontSize: 11, marginBottom: 14, lineHeight: 1.6 }}>
              Dev/preview only. Simulates a successful Stripe checkout for
              this session. Production environment will reject this.
            </div>

            {simulationPhase === "idle" && (
              <button
                className="sf-btn-secondary"
                onClick={() => setSimulationPhase("confirming")}
                data-testid="simulate-payment-button"
                style={{ borderColor: "var(--sf-gold-dim)", color: "var(--sf-gold)" }}
              >
                Simulate Successful Payment
              </button>
            )}

            {simulationPhase === "confirming" && (
              <div data-testid="simulate-payment-confirm">
                <div style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>
                  Simulate a successful payment on this audit? This creates a
                  synthetic payment record and triggers the same paid-report
                  generation the real webhook would. Cannot be undone.
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    className="sf-btn"
                    onClick={onSimulatePayment}
                    data-testid="simulate-payment-confirm-yes"
                  >
                    Yes, simulate payment
                  </button>
                  <button
                    className="sf-btn-secondary"
                    onClick={() => setSimulationPhase("idle")}
                    data-testid="simulate-payment-confirm-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {simulationPhase === "submitting" && (
              <div className="sf-muted-text" data-testid="simulate-payment-submitting">
                Simulating payment…
              </div>
            )}

            {simulationPhase === "success" && simulationResult && (
              <div data-testid="simulate-payment-success">
                <div className="sf-mono" style={{ color: "var(--sf-gold)", fontSize: 13, marginBottom: 10 }}>
                  ✓ Payment simulated · {simulationResult.report_generation}
                </div>
                <div className="sf-mono sf-muted-text" style={{ fontSize: 11, marginBottom: 12 }}>
                  Synthetic payment recorded. Report generation in progress
                  (~30–90s). Refresh the page or use the link below to view.
                </div>
                <Link
                  to={`/audit/report/${sessionId}`}
                  className="sf-btn-secondary"
                  data-testid="simulate-payment-view-report"
                >
                  View paid report
                </Link>
              </div>
            )}

            {simulationPhase === "error" && simulationErr && (
              <div
                className="sf-mono"
                style={{ color: "#e2867e", fontSize: 13 }}
                data-testid="simulate-payment-error"
              >
                {simulationErr}
                <button
                  className="sf-btn-secondary"
                  onClick={() => { setSimulationPhase("idle"); setSimulationErr(null); }}
                  style={{ marginLeft: 12 }}
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        )}

        {/* Engine-computed scoreboard (admin sees internals) */}
        {r && (
          <div className="sf-card" style={{ marginBottom: 18 }} data-testid="admin-detail-scoreboard">
            <div className="sf-eyebrow" style={{ marginBottom: 8 }}>Engine output (admin view)</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
              <div><div className="sf-eyebrow">Revenue Leak</div><div className="sf-score-number" style={{ fontSize: 32 }}>{r.revenue_leak_score}</div></div>
              <div><div className="sf-eyebrow">Opportunity</div><div className="sf-score-number" style={{ fontSize: 32 }}>{r.opportunity_score}</div></div>
              <div><div className="sf-eyebrow">Automation Readiness</div><div className="sf-score-number" style={{ fontSize: 32 }}>{r.automation_readiness_score}</div></div>
            </div>
            <div className="sf-mono sf-muted-text" style={{ fontSize: 12, marginTop: 10 }}>
              Monthly loss: ${r.estimated_monthly_loss_min?.toLocaleString()}–${r.estimated_monthly_loss_max?.toLocaleString()} · midpoint ${r.estimated_monthly_loss_midpoint?.toLocaleString()}
            </div>
            <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.7 }}>
              <strong>Preview summary:</strong> {r.preview_summary}
            </div>
            {r.detected_weaknesses?.length > 0 && (
              <ul style={{ marginTop: 12 }} data-testid="admin-detail-weaknesses">
                {r.detected_weaknesses.map((w) => (
                  <li key={`weakness-${w}`} style={{ marginBottom: 6 }}>{w}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* 12 Q&A pairs */}
        <div className="sf-card" style={{ marginBottom: 18 }} data-testid="admin-detail-answers">
          <div className="sf-eyebrow" style={{ marginBottom: 12 }}>12 diagnostic answers</div>
          {data.answers.map((a) => (
            <div key={a.question_id} style={{ marginBottom: 14, paddingBottom: 12, borderBottom: "1px dashed #22262B" }} data-testid={`admin-detail-answer-${a.question_id}`}>
              <div className="sf-mono sf-muted-text" style={{ fontSize: 11 }}>{a.question_id} · weight {a.weight}</div>
              <div style={{ fontSize: 14, marginTop: 4 }}>{a.question_text}</div>
              <div style={{ fontSize: 13, marginTop: 6, color: "var(--sf-gold)" }}>→ {a.answer}</div>
            </div>
          ))}
        </div>

        {/* Payment */}
        {p && (
          <div className="sf-card" style={{ marginBottom: 18 }} data-testid="admin-detail-payment">
            <div className="sf-eyebrow" style={{ marginBottom: 8 }}>Payment</div>
            <div className="sf-mono" style={{ fontSize: 12, lineHeight: 1.7 }}>
              <div>Payment ID: {p.payment_id}</div>
              <div>Amount: ${(p.amount_cents / 100).toFixed(2)} {p.currency?.toUpperCase()}</div>
              <div>Status: {p.status}</div>
              <div>Paid at: {p.paid_at || "—"}</div>
              <div>Stripe event: {p.stripe_event_id || "—"}</div>
              <div>Settlement source: {p.settlement_source || "—"}</div>
            </div>
          </div>
        )}

        {/* Implementation request */}
        {ir && (
          <div className="sf-card" style={{ marginBottom: 18 }} data-testid="admin-detail-impl-request">
            <div className="sf-eyebrow" style={{ marginBottom: 8 }}>Implementation request</div>
            <div className="sf-mono" style={{ fontSize: 12, lineHeight: 1.7 }}>
              <div>Request ID: {ir.request_id}</div>
              <div>Name: {ir.name} · Email: {ir.email}</div>
              <div>Business: {ir.business}</div>
              <div>Tier: {ir.preferred_tier} · Status: <strong style={{ color: "var(--sf-gold)" }}>{ir.status}</strong></div>
            </div>
            <div style={{ marginTop: 10, fontSize: 13 }}>
              <strong>Notes from customer:</strong> {ir.notes || "—"}
            </div>
            {ir.internal_notes && (
              <div style={{ marginTop: 8, fontSize: 13 }}>
                <strong style={{ color: "var(--sf-gold)" }}>Internal notes:</strong> {ir.internal_notes}
              </div>
            )}
            <Link to="/admin/implementation-requests" className="sf-btn-secondary" style={{ display: "inline-block", marginTop: 12 }}>
              Update status →
            </Link>
          </div>
        )}

        {/* 13 paid sections (admin view) */}
        {r?.full_report?.sections && (
          <div className="sf-card" data-testid="admin-detail-full-report">
            <div className="sf-eyebrow" style={{ marginBottom: 12 }}>Paid full report ({r.full_report.sections.length} sections)</div>
            {r.full_report.sections.map((sec, idx) => (
              <div key={sec.id} style={{ marginBottom: 16, paddingBottom: 14, borderBottom: "1px dashed #22262B" }} data-testid={`admin-detail-section-${sec.id}`}>
                <div className="sf-mono sf-muted-text" style={{ fontSize: 11 }}>SECTION {String(idx + 1).padStart(2, "0")}</div>
                <div style={{ fontSize: 16, marginTop: 4 }}>{sec.title}</div>
                <div style={{ fontSize: 13, marginTop: 8, lineHeight: 1.7, whiteSpace: "pre-line" }}>{sec.content}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default function AdminAuditDetail() {
  return <AdminAuthGuard>{(admin) => <Inner admin={admin} />}</AdminAuthGuard>;
}
